import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { AppShell, type AppShellContext } from "@/components/app/AppShell";
import { api } from "@/lib/api";

type UpdateResponse = {
  ok: true;
  brand: {
    id: string;
    name: string;
    storefrontUrl: string | null;
    storefrontDiscoveryStatus: "none" | "discovering" | "done" | "failed";
  };
};

export default function DashboardBrandEdit() {
  return <AppShell>{(ctx) => <Inner ctx={ctx} />}</AppShell>;
}

function Inner({ ctx }: { ctx: AppShellContext }) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const brand = ctx.brands.find((b) => b.id === id);

  const [name, setName] = useState(brand?.name ?? "");
  const [storefrontUrl, setStorefrontUrl] = useState(brand?.storefrontUrl ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep form in sync when ctx.brands hydrates after the initial render
  useEffect(() => {
    if (!brand) return;
    setName(brand.name);
    setStorefrontUrl(brand.storefrontUrl ?? "");
  }, [brand?.id, brand?.name, brand?.storefrontUrl, brand]);

  if (!id) {
    return null;
  }

  if (!brand) {
    return (
      <div className="mx-auto w-full max-w-2xl px-6 py-16 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          Brand not found
        </h1>
        <Link
          to="/dashboard/brands"
          className="mt-4 inline-flex text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
        >
          ← Back to brands
        </Link>
      </div>
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmedName = name.trim();
    const trimmedUrl = storefrontUrl.trim();
    const body: { name?: string; storefrontUrl?: string } = {};
    if (trimmedName && trimmedName !== brand?.name) body.name = trimmedName;
    if (trimmedUrl && trimmedUrl !== (brand?.storefrontUrl ?? ""))
      body.storefrontUrl = trimmedUrl;
    if (Object.keys(body).length === 0) {
      navigate("/dashboard/brands");
      return;
    }
    setSubmitting(true);
    const res = await api<UpdateResponse>(`/customer/brands/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error ?? "Couldn't update the brand.");
      return;
    }
    navigate("/dashboard/brands");
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-6 py-10">
      <Link
        to="/dashboard/brands"
        className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
      >
        ← Brands
      </Link>
      <h1 className="mt-4 text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
        Edit brand
      </h1>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
        Rename your brand or swap its storefront URL. Changing the URL
        re-scans for products.
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Brand name
          </label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Amazon storefront URL
          </label>
          <input
            type="url"
            value={storefrontUrl}
            onChange={(e) => setStorefrontUrl(e.target.value)}
            placeholder="https://www.amazon.com/stores/YourBrand/page/…"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Changing this re-scans your products from the new storefront.
            Existing products stay on the brand until you remove them.
          </p>
        </div>

        {error && (
          <p
            className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            <AlertTriangle className="h-4 w-4" /> {error}
          </p>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? "Saving…" : "Save changes"}
          </button>
          <Link
            to="/dashboard/brands"
            className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
